import { AxiosError } from "axios"
import { MarkInvoicePaidManuallyRequest } from "../../../domain/invoicing/invoicingApi"
import { MarkStatementPaidManuallyRequest } from "../../../domain/invoicing/statementsApi"
import { InvoicePaymentStatus, PaymentMethodSummary } from "../../../domain/invoicing/payment"
import { hasValue } from "../../../helpers/nullable"

export type InvoiceOption = 'methodOnFile' | 'markPaidManually'

export type ManualPaymentMethod = 'cash' | 'check'

// Keep aligned with the API's ManualPaymentInput.MaxCheckNumberLength (SYN-3885).
export const CHECK_NUMBER_MAX_LENGTH = 100

// Match .NET String.Trim: unlike JS trim, this includes U+0085 and preserves U+FEFF.
export const normalizeCheckNumber = (value: string): string =>
  value.replace(/^[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+|[\t\n\v\f\r \u0085\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]+$/g, '')

export const checkNumberLengthError = (value: string): string | undefined =>
  normalizeCheckNumber(value).length > CHECK_NUMBER_MAX_LENGTH
    ? `Check number must be ${CHECK_NUMBER_MAX_LENGTH} characters or fewer.`
    : undefined

export type PayInvoiceState = {
  option: InvoiceOption
  paymentMethodId: string
  manualPaymentMethod: ManualPaymentMethod
  checkNumber: string
}

export const isStateValidForSubmit = (state: PayInvoiceState, status?: InvoicePaymentStatus): boolean => {
  if (state.option === 'methodOnFile') {
    return state.paymentMethodId != '' && isStatusValidForPaymentMethodOnFile(status)
  }
  if (state.manualPaymentMethod === 'check') {
    return normalizeCheckNumber(state.checkNumber) !== '' && !checkNumberLengthError(state.checkNumber)
  }
  return true
}

const isStatusValidForPaymentMethodOnFile = (status?: InvoicePaymentStatus): boolean => {
  return status?.isMerchantActive === true
    && hasValue(status?.paymentId)
    && status?.paymentStatus === 'ready'
}

export const shouldAllowPaymentMethodOnFile = (paymentMethods: PaymentMethodSummary[], status: InvoicePaymentStatus | undefined): boolean => {
  return isStatusValidForPaymentMethodOnFile(status)
    && paymentMethods.length > 0
}

export const isAlreadyPaid = (status?: InvoicePaymentStatus): boolean => {
  return hasValue(status) && hasValue(status.paymentStatus) && status.paymentStatus !== 'ready'
} 

export const canPaymentMethodBeUsed = (paymentMethod: PaymentMethodSummary): boolean => {
  return paymentMethod.isActive && !paymentMethod.isExpired
}

export const getFirstPaymentIdWhichCanBeUsed = (paymentMethods: PaymentMethodSummary[]): string => {
  return paymentMethods.find(x => canPaymentMethodBeUsed(x))?.id ?? ''
}

export const resolveAutoSelectedPayState = (
  paymentMethods: PaymentMethodSummary[],
  status: InvoicePaymentStatus | undefined
): Pick<PayInvoiceState, 'option' | 'paymentMethodId'> => ({
  option: shouldAllowPaymentMethodOnFile(paymentMethods, status) ? 'methodOnFile' : 'markPaidManually',
  paymentMethodId: getFirstPaymentIdWhichCanBeUsed(paymentMethods)
})

export const paymentMethodOnFileErrorMessage = (error: unknown): string =>
  ((error as AxiosError)?.response?.data as any)?.errorMessage ?? 'The payment could not be created at this time.'

export const markPaidManuallyErrorMessage = (error: unknown, documentLabel: string): string => {
  const data = (error as AxiosError)?.response?.data as any
  return data?.errors?.join('\n') ?? data?.message ?? `The ${documentLabel.toLowerCase()} could not be marked as paid at this time.`
}

export const toMarkPaidManuallyRequest = (invoiceId: string, state: PayInvoiceState): MarkInvoicePaidManuallyRequest => ({
  invoiceId,
  paymentMethod: state.manualPaymentMethod,
  checkNumber: state.manualPaymentMethod === 'check' ? normalizeCheckNumber(state.checkNumber) : undefined
})

export const toMarkStatementPaidManuallyRequest = (statementId: string, state: PayInvoiceState): MarkStatementPaidManuallyRequest => ({
  statementId,
  paymentMethod: state.manualPaymentMethod,
  checkNumber: state.manualPaymentMethod === 'check' ? normalizeCheckNumber(state.checkNumber) : undefined
})

export const buildPaymentMethodSecondaryText = (paymentMethod: PaymentMethodSummary): string => {
  const type = paymentMethod.methodType === 'ach' || paymentMethod.methodType === 'validatedAch'
    ? 'Bank account'
    : paymentMethod.name

  const expires = paymentMethod.expirationMonth && paymentMethod.expirationYear
    ? ` | Expires ${paymentMethod.expirationMonth}/${paymentMethod.expirationYear}`
    : ''

  return `${type} ending in ${paymentMethod.lastFour}${expires}`
}
